import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import type { QuoteBlockAttsInterface } from '~/types/AppData/Blocks/QuoteBlockInterface';
import { AppDataContext } from '~/contexts/AppDataContext';
import { cva } from 'class-variance-authority';
import {
  getCSSColorBorder,
  getCSSColorFill,
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
  getCSSTextAlign,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import QuoteIcon from '~/icons/quote';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { useBlockClasses } from '~/utilities/useBlockClasses';

const cssClasses = cva('', {
  variants: {
    borderColor: getCSSColorBorder(),
    border: {
      true: 'border-b border-solid',
    },
    isPremium: {
      true: 'lg:max-w-[75%] lg:mx-auto font-normal font-semibold italic',
    },
  },
});

const textCssClasses = cva('italic', {
  variants: {
    color: getCSSColorText(),
    align: getCSSTextAlign(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
    isPremium: {
      true: 'font-group-normal !text-[28px] !font-[700]',
    },
  },
});

const citeCssClasses = cva('font-bold text-sm md:text-base block', {
  variants: {
    color: getCSSColorText(),
    align: getCSSTextAlign(),
  },
});

const cssIconClasses = cva('w-4 h-4 lg:w-6 lg:h-6 basis-10 lg:basis-14', {
  variants: {
    fill: getCSSColorFill(),
  },
});

const cssBorderClasses = cva(
  `content-[""] border-t border-t-solid h-[1px] flex-[2]`,
  {
    variants: {
      border: getCSSColorBorder(),
    },
  }
);

export default component$(
  ({ text, cite }: QuoteBlockAttsInterface): JSX.Element => {
    const { themeComponents } = useContext(AppDataContext);
    const {
      quoteTextColor,
      quoteCiteColor,
      quoteIconColor,
      quoteShowApostrophes,
      quoteShowIconAndBorder,
      quoteAlignment,
      quoteShowCite,
      quoteBorderColor,
    } = themeComponents.articleBlock;

    const isPremium = useIsPremiumTemplate();
    const blockClasses = useBlockClasses();
    // global css classes are conflicting with the quote block css classes
    const html = text.replaceAll(/(<p>|<\/p>)/g, '');

    return (
      <blockquote
        class={cssClasses({
          isPremium,
          border: quoteShowIconAndBorder.value,
          borderColor: quoteBorderColor.value,
          class: blockClasses,
        })}
      >
        {quoteShowIconAndBorder.value && (
          <div class='flex w-full items-center'>
            <div class={cssBorderClasses({ border: quoteBorderColor.value })} />
            <QuoteIcon
              cssClasses={cssIconClasses({
                fill: quoteIconColor.value,
              })}
            />
            <div class={cssBorderClasses({ border: quoteBorderColor.value })} />
          </div>
        )}
        <div
          class={textCssClasses({
            isPremium,
            align: quoteAlignment.value.toLowerCase() as
              | 'left'
              | 'center'
              | 'right'
              | 'justify',
            color: quoteTextColor.value,
            ...getFontSizeObject(themeComponents.articleBlock, 'quoteFontSize'),
          })}
          dangerouslySetInnerHTML={
            quoteShowApostrophes.value ? `"${html}"` : html
          }
        />
        {quoteShowCite.value && cite && (
          <cite
            class={citeCssClasses({
              color: quoteCiteColor.value,
              align: quoteAlignment.value.toLowerCase() as
                | 'left'
                | 'center'
                | 'right'
                | 'justify',
            })}
          >
            {cite}
          </cite>
        )}
      </blockquote>
    );
  }
);
